import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ssoService } from '../../services/ssoService';
import { teamService } from '../../services/teamService';
import NavigationSidebar from '../../components/NavigationSidebar';
import ProviderSetup from './components/ProviderSetup';
import DomainVerification from './components/DomainVerification';
import AttributeMapping from './components/AttributeMapping';
import ConnectionStatus from './components/ConnectionStatus';
import { CheckCircle, AlertTriangle } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';

const SSOSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [ssoConfigs, setSsoConfigs] = useState([]);
  const [activeTab, setActiveTab] = useState('okta');
  const [providerStatus, setProviderStatus] = useState([]);

  useEffect(() => {
    if (user?.id) {
      loadOrganizations();
    }
  }, [user]);

  useEffect(() => {
    if (selectedOrg?.organization_id) {
      loadSSOConfigurations();
      loadProviderStatus();
    }
  }, [selectedOrg]);

  const loadOrganizations = async () => {
    setLoading(true);
    try {
      const { data, error } = await teamService?.getUserTeams(user?.id);
      if (!error && data) {
        setOrganizations(data);
        const adminOrg = data?.find(org => org?.member_role === 'owner' || org?.member_role === 'admin');
        if (adminOrg) {
          setSelectedOrg(adminOrg);
        }
      }
    } catch (error) {
      console.error('Error loading organizations:', error);
      toast?.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const loadSSOConfigurations = async () => {
    try {
      const { data, error } = await ssoService?.getSSOConfigurations(selectedOrg?.organization_id);
      if (!error && data) {
        setSsoConfigs(data);
      }
    } catch (error) {
      console.error('Error loading SSO configurations:', error);
    }
  };

  const loadProviderStatus = async () => {
    try {
      const { data, error } = await ssoService?.getProviderStatus(selectedOrg?.organization_id);
      if (!error && data) {
        setProviderStatus(data);
      }
    } catch (error) {
      console.error('Error loading provider status:', error);
    }
  };

  const handleProviderSave = async (provider, config) => {
    try {
      // Validate SAML metadata before saving
      const validationErrors = validateSAMLMetadata(config);
      if (validationErrors?.length > 0) {
        toast?.error(`Validation failed: ${validationErrors?.join(', ')}`);
        return;
      }

      const existingConfig = ssoConfigs?.find(c => c?.provider === provider);
      
      if (existingConfig) {
        const { error } = await ssoService?.updateSSOConfiguration(existingConfig?.id, config);
        if (error) throw error;
        toast?.success(`${provider} configuration updated successfully`);
      } else {
        const { error } = await ssoService?.createSSOConfiguration(
          selectedOrg?.organization_id,
          provider,
          config
        );
        if (error) throw error;
        toast?.success(`${provider} configuration created successfully`);
      }
      
      loadSSOConfigurations();
    } catch (error) {
      console.error('Error saving SSO configuration:', error);
      toast?.error(error?.message || 'Failed to save configuration');
    }
  };

  const handleTestConnection = async (provider) => {
    try {
      const config = ssoConfigs?.find(c => c?.provider === provider);
      if (!config) {
        toast?.error('Configuration not found');
        return;
      }

      // Validate SAML metadata before testing
      const validationErrors = validateSAMLMetadata(config);
      if (validationErrors?.length > 0) {
        toast?.error(`Validation failed: ${validationErrors?.join(', ')}`);
        return;
      }

      const { error } = await ssoService?.testSSOConnection(config?.id);
      if (error) throw error;
      
      toast?.success('Connection test successful');
      loadSSOConfigurations();
      loadProviderStatus();
    } catch (error) {
      console.error('Error testing connection:', error);
      toast?.error(error?.message || 'Connection test failed');
    }
  };

  const validateSAMLMetadata = (config) => {
    const errors = [];

    // Validate Entity ID
    if (!config?.entity_id || config?.entity_id?.trim() === '') {
      errors?.push('Entity ID is required');
    } else if (!/^https?:\/\/.+/?.test(config?.entity_id)) {
      errors?.push('Entity ID must be a valid URL');
    }

    // Validate SSO URL
    if (!config?.sso_url || config?.sso_url?.trim() === '') {
      errors?.push('SSO URL is required');
    } else if (!/^https:\/\/.+/?.test(config?.sso_url)) {
      errors?.push('SSO URL must be a valid HTTPS URL');
    }

    // Validate Certificate
    if (!config?.certificate || config?.certificate?.trim() === '') {
      errors?.push('Certificate is required');
    } else {
      // Check certificate format (should start with BEGIN CERTIFICATE)
      const certContent = config?.certificate?.trim();
      if (!certContent?.includes('BEGIN CERTIFICATE') || !certContent?.includes('END CERTIFICATE')) {
        errors?.push('Certificate must be in valid PEM format');
      }
    }

    return errors;
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <NavigationSidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading SSO settings...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedOrg || (selectedOrg?.member_role !== 'owner' && selectedOrg?.member_role !== 'admin')) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Toaster position="top-right" />
        <NavigationSidebar />
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">Access Restricted</h2>
            <p className="text-muted-foreground mb-6">
              SSO settings are only available to organization owners and administrators.
            </p>
            <button
              onClick={() => window.history?.back()}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentConfig = ssoConfigs?.find(c => c?.provider === activeTab);

  return (
    <div className="flex h-screen bg-gray-50">
      <Toaster position="top-right" />
      <NavigationSidebar />
      <div className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto p-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center p-2">
                <img 
                  src="/assets/images/Photo_Feb_21_2026_12_00_04_AM_1_-1771799585955.png" 
                  alt="DraftIQ Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">SSO Settings</h1>
                <p className="text-muted-foreground">Configure enterprise single sign-on authentication</p>
              </div>
            </div>
          </div>

          {/* Organization Selector */}
          {organizations?.length > 1 && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground mb-2">
                Organization
              </label>
              <select
                value={selectedOrg?.organization_id}
                onChange={(e) => {
                  const org = organizations?.find(o => o?.organization_id === e?.target?.value);
                  setSelectedOrg(org);
                }}
                className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {organizations?.map(org => (
                  <option key={org?.organization_id} value={org?.organization_id}>
                    {org?.organization_name} ({org?.member_role})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Provider Tabs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
            <div className="border-b border-gray-200">
              <div className="flex">
                {['okta', 'azure_ad', 'google_workspace']?.map(provider => {
                  const status = providerStatus?.find(s => s?.provider === provider);
                  const isActive = status?.connectionStatus === 'active';
                  
                  return (
                    <button
                      key={provider}
                      onClick={() => setActiveTab(provider)}
                      className={`flex-1 px-6 py-4 text-sm font-medium transition-colors relative ${
                        activeTab === provider
                          ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <span>
                          {provider === 'okta' && 'Okta'}
                          {provider === 'azure_ad' && 'Azure AD'}
                          {provider === 'google_workspace' && 'Google Workspace'}
                        </span>
                        {isActive && (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              <ProviderSetup
                provider={activeTab}
                config={currentConfig}
                onSave={handleProviderSave}
                onTest={handleTestConnection}
              />
              
              {/* Test Connection Button */}
              {currentConfig && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => handleTestConnection(activeTab)}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    Test SSO Connection
                  </button>
                  <p className="text-sm text-muted-foreground mt-2">
                    Test the SAML configuration to ensure it's working correctly
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Domain Verification */}
          {currentConfig && (
            <div className="mb-6">
              <DomainVerification
                organizationId={selectedOrg?.organization_id}
                ssoConfigId={currentConfig?.id}
                onUpdate={loadSSOConfigurations}
              />
            </div>
          )}

          {/* Attribute Mapping */}
          {currentConfig && (
            <div className="mb-6">
              <AttributeMapping
                config={currentConfig}
                onSave={(mapping) => handleProviderSave(activeTab, { attribute_mapping: mapping })}
              />
            </div>
          )}

          {/* Connection Status Dashboard */}
          <ConnectionStatus
            organizationId={selectedOrg?.organization_id}
            providerStatus={providerStatus}
          />
        </div>
      </div>
    </div>
  );
};

export default SSOSettings;